import { LoginResponse, type Role, User } from "@el/schema";
import { SYNC_MESSAGES } from "@el/sync";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { ApiFailure, api, setUnauthorizedHandler } from "./api";
import { getLocal } from "./db";
import { deviceId } from "./ids";
import { getSync } from "./sync";

/**
 * 入口（2026-10-14 本人の指示で登録とログインを削除）。セッションが無ければ POST /api/auth/demo で
 * パスワードなしのデモ管理者として入る。入れないとき（503・つながらない）は failed（接続できない画面）。
 * demo: デモの利用者（user.demo）
 */
export type AuthState = { status: "loading" } | { status: "failed"; reason?: string } | { status: "signedIn"; user: User; offline: boolean; demo?: boolean };

type AuthApi = {
  state: AuthState;
  /** 接続できない画面の「再試行」 */
  retry(): void;
};

const Ctx = createContext<AuthApi | null>(null);

export const AUTH_PATHS = { demo: "/api/auth/demo", me: "/api/auth/me" } as const;

const DEFAULT_GRACE_DAYS = 14;
export const MSG_GRACE_EXPIRED = "オフラインで使える期間を過ぎました。電波のある所で再読み込みしてください。";

async function readCachedUser(): Promise<{ user: User; graceDays: number } | null> {
  const { db } = getLocal();
  const [u, g] = await Promise.all([db.meta.get("user"), db.meta.get("offlineGraceDays")]);
  if (!u) return null;
  try {
    const user = User.parse(JSON.parse(u.value));
    const graceDays = Number(g?.value ?? DEFAULT_GRACE_DAYS);
    return { user, graceDays: Number.isFinite(graceDays) && graceDays > 0 ? graceDays : DEFAULT_GRACE_DAYS };
  } catch {
    return null;
  }
}

async function cacheUser(user: User, graceDays?: number) {
  const { db, store } = getLocal();
  const prev = await store.getMeta("userId");
  // 前と違う利用者で入ったら、前の利用者のデータを残さない
  if (prev && prev !== user.id) await store.clearAll();
  await db.meta.bulkPut([{ key: "user", value: JSON.stringify(user) }, { key: "userId", value: user.id }, ...(graceDays ? [{ key: "offlineGraceDays", value: String(graceDays) }] : [])]);
}

/** 入れない。reason はふつうの「再読み込みしてください」の代わりに出す理由 */
const failed = (reason?: string): AuthState => (reason ? { status: "failed", reason } : { status: "failed" });

/** 入口の回数の上限（429 demo_rate_limited。同じ IP で 1 分に 10 回まで）。API が文言を返さないときに使う */
export const MSG_DEMO_RATE_LIMITED = "続けてお試しいただいた回数が多いため、しばらく受け付けられません。1 分ほど待ってからもう一度お試しください。";

/**
 * デモ管理者として入る。入れなければ（503・つながらない・形が違う）null。
 * 回数の上限（429）は、API が返す文言を理由にした failed を返す（接続できない画面にそのまま出す）
 */
export async function enterDemo(): Promise<AuthState | null> {
  try {
    const res = await api.post(AUTH_PATHS.demo, { client: "web", deviceId: deviceId() }, LoginResponse);
    await cacheUser(res.user, res.offlineGraceDays);
    return { status: "signedIn", user: res.user, offline: false, demo: !!res.user.demo };
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 429) return failed(e.code === "demo_rate_limited" && e.message ? e.message : MSG_DEMO_RATE_LIMITED);
    return null;
  }
}

/**
 * 起動時: この端末で入ったことが無ければデモに入る。入ったことがあれば /me、
 * つながらなければ端末に残した利用者で、最後の同期から期限内なら開く。セッションが切れていたら入り直す
 */
export async function resolveSession(): Promise<AuthState> {
  if (!(await readCachedUser())) return (await enterDemo()) ?? failed();
  try {
    const res = await api.get(AUTH_PATHS.me, z.object({ user: User }));
    await cacheUser(res.user);
    return { status: "signedIn", user: res.user, offline: false, demo: !!res.user.demo };
  } catch (e) {
    if (e instanceof ApiFailure && e.status === 401) return (await enterDemo()) ?? failed();
    // つながらない・サーバーの不調でも、端末のデータで使えるようにする
    const cached = await readCachedUser();
    if (!cached) return failed();
    if (!(await getSync().offlineAllowed(cached.graceDays))) return failed(MSG_GRACE_EXPIRED);
    return { status: "signedIn", user: cached.user, offline: true, demo: !!cached.user.demo };
  }
}

/** セッションが切れた・端末のデータを消すよう言われたとき: 入り直す（入れなければ接続できない画面） */
async function reenter(setState: (s: AuthState) => void, wipe = false) {
  if (wipe) await getLocal().store.clearAll();
  setState((await enterDemo()) ?? failed());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 再試行（attempt）のたびに確かめ直す
  useEffect(() => {
    let alive = true;
    void resolveSession().then((s) => alive && setState(s));
    return () => {
      alive = false;
    };
  }, [attempt]);

  useEffect(() => {
    // 停止・権限の変更（account_disabled）は端末のデータを消してから入り直す
    setUnauthorizedHandler((code) => void reenter(setState, code === "account_disabled"));
    return () => setUnauthorizedHandler(null);
  }, []);

  // 入っている間だけ同期を回す
  useEffect(() => {
    if (state.status !== "signedIn") return;
    const sync = getSync();
    sync.start();
    const off = sync.onStatus((s) => {
      if (s.state !== "error") return;
      if (s.message === SYNC_MESSAGES.relogin) void reenter(setState);
      else if (s.message === SYNC_MESSAGES.wiped) void reenter(setState, true);
    });
    return () => {
      off();
      sync.stop();
    };
  }, [state.status]);

  const retry = useCallback(() => {
    setState({ status: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  const value = useMemo(() => ({ state, retry }), [state, retry]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider の外で useAuth を呼んでいます");
  return v;
}

/** 入っている利用者（入った後の画面だけで使う） */
export function useMe(): User {
  const { state } = useAuth();
  if (state.status !== "signedIn") throw new Error("まだ入っていません");
  return state.user;
}

/** デモ（ログインなし）で入っているか */
export function useDemo(): boolean {
  const { state } = useAuth();
  return state.status === "signedIn" && !!state.demo;
}

/** デモの利用者には API が 403（demo_forbidden）を返すので、押せない状態にして出す理由 */
export const DEMO_UNAVAILABLE = "デモでは使えません";

/** 権限ごとにできること（docs/architecture.md 5 章。API でも検査する） */
export const can = {
  editMaster: (r: Role) => r === "admin" || r === "master",
  importFiles: (r: Role) => r === "admin" || r === "master",
  checkValues: (_r: Role) => true,
  manageRules: (r: Role) => r === "admin",
  manageAi: (r: Role) => r === "admin",
  seeCost: (r: Role) => r === "admin" || r === "master",
};

export const ROLE_LABEL: Record<Role, string> = { admin: "管理者", master: "マスタ担当", sales: "営業" };
